import { useState } from 'react';
import { Upload, Image, message as antdMessage } from 'antd';
import {
    AudioOutlined,
    BookOutlined,
    CodeOutlined,
    DeleteOutlined,
    EyeOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FilePptOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
    LoadingOutlined,
    PlusOutlined,
    VideoCameraOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';
import { isImageFile } from '../../../../../Utils/nativeFileUpload';

const isImageUploadFile = (file = {}) => {
    if (file.isImage) return true;

    const mimeType = file.type || file.mime_type || file.fileData?.mime_type || '';
    if (mimeType.startsWith('image/')) return true;

    return isImageFile({
        type: mimeType,
        mime_type: mimeType,
        name: file.name || file.fileData?.original_filename || file.fileData?.filename
    }, file.url || file.thumbUrl || file.fileData?.file_url);
};

const getFileTypeInfo = (file = {}) => {
    const mimeType = file.type || file.mime_type || file.fileData?.mime_type || '';
    const name = file.name || file.fileData?.original_filename || file.fileData?.filename || '';

    if (isImageUploadFile(file)) {
        return { icon: FileImageOutlined, color: '#0891b2' };
    }

    if (
        mimeType.startsWith('audio/') ||
        mimeType === 'application/ogg' ||
        /\.(mp3|wav|ogg|m4a|flac|aac|weba)$/i.test(name) ||
        /(?:^|_)voice_\d+\.webm$/i.test(name)
    ) {
        return { icon: AudioOutlined, color: '#db2777' };
    }

    if (mimeType.startsWith('video/') || /\.(mp4|webm|avi|mov|mkv|flv|wmv)$/i.test(name)) {
        return { icon: VideoCameraOutlined, color: '#7c3aed' };
    }

    if (
        mimeType.includes('wordprocessingml') ||
        mimeType === 'application/msword' ||
        mimeType === 'application/vnd.oasis.opendocument.text' ||
        mimeType === 'application/rtf' ||
        /\.(doc|docx|odt|rtf)$/i.test(name)
    ) {
        return { icon: FileWordOutlined, color: '#1677ff' };
    }

    if (
        mimeType.includes('spreadsheetml') ||
        mimeType === 'application/vnd.ms-excel' ||
        mimeType === 'application/vnd.oasis.opendocument.spreadsheet' ||
        mimeType === 'text/csv' ||
        /\.(xls|xlsx|ods|csv)$/i.test(name)
    ) {
        return { icon: FileExcelOutlined, color: '#16a34a' };
    }

    if (
        mimeType.includes('presentationml') ||
        mimeType === 'application/vnd.ms-powerpoint' ||
        mimeType === 'application/vnd.oasis.opendocument.presentation' ||
        /\.(ppt|pptx|odp)$/i.test(name)
    ) {
        return { icon: FilePptOutlined, color: '#ea580c' };
    }

    if (mimeType === 'application/pdf' || /\.pdf$/i.test(name)) {
        return { icon: FilePdfOutlined, color: '#ef4444' };
    }

    if (mimeType === 'application/epub+zip' || mimeType === 'application/x-mobipocket-ebook' || /\.(epub|mobi)$/i.test(name)) {
        return { icon: BookOutlined, color: '#9333ea' };
    }

    if (mimeType.includes('json') || mimeType.includes('xml') || mimeType.includes('yaml') || /\.(json|xml|yaml|yml)$/i.test(name)) {
        return { icon: CodeOutlined, color: '#0f766e' };
    }

    if (mimeType.includes('zip') || mimeType.includes('rar') || mimeType.includes('7z') || mimeType.includes('tar') || mimeType.includes('gzip') || /\.(zip|rar|7z|tar|gz)$/i.test(name)) {
        return { icon: FileZipOutlined, color: '#a16207' };
    }

    if (mimeType.startsWith('text/') || /\.(txt|md|rtf)$/i.test(name)) {
        return { icon: FileTextOutlined, color: '#475569' };
    }

    return { icon: FileOutlined, color: '#1677ff' };
};

const renderFileIcon = (file, isDarkMode) => {
    const { icon: Icon, color } = getFileTypeInfo(file);
    return (
        <Icon
            data-testid={`attachment-type-icon-${file?.uid || file?.fileId || 'file'}`}
            style={{ fontSize: 36, color: isDarkMode ? '#8ab4ff' : color }}
        />
    );
};

const getImageSource = (file = {}) => file.thumbUrl || file.url || file.fileData?.file_url || null;

const footerPreviewTileStyles = `
    .message-attachment-tile .message-attachment-actions {
        opacity: 1;
        pointer-events: auto;
        transition: opacity 160ms ease;
    }

    @media (hover: hover) and (pointer: fine) {
        .message-attachment-tile .message-attachment-actions {
            opacity: 0;
            pointer-events: none;
        }

        .message-attachment-tile:hover .message-attachment-actions,
        .message-attachment-tile:focus-within .message-attachment-actions {
            opacity: 1;
            pointer-events: auto;
        }
    }
`;

const FooterPreview = ({
    fileList,
    setFileList,
    handlePreview,
    handleChange,
    handleFileUpload,
    handleBulkUpload,
    accept,
    uploading,
    previewOpen,
    previewImage,
    setPreviewOpen,
    setPreviewImage,
    pendingVoice
}) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [imageLoadErrors, setImageLoadErrors] = useState({});
    const [deletingFileIds, setDeletingFileIds] = useState({});

    const handleBeforeUpload = async (file, selectedFiles = []) => {
        const files = Array.from(selectedFiles || []);
        if (files.length > 1) {
            const firstFile = files[0];
            if (typeof handleBulkUpload === 'function' && (file === firstFile || file?.uid === firstFile?.uid)) {
                await handleBulkUpload(files);
            }
            return false;
        }

        const uploadedFile = await handleFileUpload(file);
        if (uploadedFile) {
            setFileList((prev) => [...prev, uploadedFile]);
        }
        return false;
    };

    const handleRemoveFile = async (file) => {
        if (!file?.uid || deletingFileIds[file.uid]) return;

        setDeletingFileIds((prev) => ({ ...prev, [file.uid]: true }));
        try {
            const result = await handleChange({ file: { ...file, status: 'removed' } }, file);
            if (result?.success !== false) {
                antdMessage.success(t('file_upload.file_deleted', { defaultValue: 'File deleted successfully' }));
            }
        } finally {
            setDeletingFileIds((prev) => {
                const next = { ...prev };
                delete next[file.uid];
                return next;
            });
        }
    };

    const renderAttachmentTile = (file) => {
        const imageSrc = getImageSource(file);
        const shouldRenderImage = isImageUploadFile(file) && imageSrc && !imageLoadErrors[file.uid];
        const fileName = file.name || 'file';
        const isDeleting = Boolean(deletingFileIds[file.uid]);

        return (
            <div
                className="message-attachment-tile"
                data-testid={`attachment-tile-${file.uid}`}
                style={{
                    width: '104px',
                    height: '104px',
                    border: `1px solid ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                    borderRadius: 8,
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: isDarkMode ? '#1f1f1f' : '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <button
                    type="button"
                    aria-label={fileName}
                    onClick={() => handlePreview(file)}
                    style={{
                        border: 0,
                        padding: 0,
                        margin: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isDarkMode ? '#f5f5f5' : '#111827',
                    }}
                >
                    {shouldRenderImage ? (
                        <img
                            data-testid={`attachment-image-${file.uid}`}
                            src={imageSrc}
                            alt={fileName}
                            onError={() => setImageLoadErrors((prev) => ({ ...prev, [file.uid]: true }))}
                            style={{
                                width: '100%',
                                height: 68,
                                objectFit: 'cover',
                                display: 'block',
                                borderBottom: `1px solid ${isDarkMode ? '#303030' : '#f0f0f0'}`,
                            }}
                        />
                    ) : (
                        <div style={{ height: 68, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {renderFileIcon(file, isDarkMode)}
                        </div>
                    )}
                    <div
                        title={fileName}
                        style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: 12,
                            lineHeight: '16px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                        }}
                    >
                        {fileName}
                    </div>
                </button>

                <div
                    className="message-attachment-actions"
                    style={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        display: 'flex',
                        gap: 4,
                    }}
                >
                    <button
                        type="button"
                        aria-label={`Preview ${fileName}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            handlePreview(file);
                        }}
                        style={{
                            width: 24,
                            height: 24,
                            border: 0,
                            borderRadius: 4,
                            background: 'rgba(0, 0, 0, 0.45)',
                            color: '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        <EyeOutlined />
                    </button>
                    <button
                        type="button"
                        aria-label={`Remove ${fileName}`}
                        disabled={isDeleting}
                        onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveFile(file);
                        }}
                        style={{
                            width: 24,
                            height: 24,
                            border: 0,
                            borderRadius: 4,
                            background: 'rgba(0, 0, 0, 0.45)',
                            color: '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: isDeleting ? 'wait' : 'pointer',
                            opacity: isDeleting ? 0.85 : 1,
                        }}
                    >
                        {isDeleting ? <LoadingOutlined /> : <DeleteOutlined />}
                    </button>
                </div>
            </div>
        );
    };

    if (fileList.length === 0 || pendingVoice) return null;

    return (
        <>
            <style>{footerPreviewTileStyles}</style>
            <div style={{ marginBottom: '12px', width: '100%' }}>
                <div style={{ overflowX: 'auto', overflowY: 'hidden', padding: '4px', WebkitOverflowScrolling: 'touch' }}>
                    <div style={{ display: 'inline-flex', gap: '8px', paddingBottom: '10px' }}>
                        {fileList.map((file) => (
                            <div key={file.uid} style={{ width: '104px', height: '104px', flexShrink: 0 }}>
                                <Upload
                                    listType="picture-card"
                                    fileList={[file]}
                                    onPreview={() => handlePreview(file)}
                                    onChange={(info) => handleChange(info, file)}
                                    showUploadList={{
                                        showPreviewIcon: true,
                                        showRemoveIcon: true,
                                        showDownloadIcon: false,
                                    }}
                                    isImageUrl={isImageUploadFile}
                                    iconRender={(uploadFile) => renderFileIcon(uploadFile, isDarkMode)}
                                    itemRender={(_, uploadFile) => renderAttachmentTile(uploadFile)}
                                    style={{ width: '104px', height: '104px' }}
                                />
                            </div>
                        ))}
                        {fileList.length < 8 && (
                            <div style={{ width: '104px', height: '104px', flexShrink: 0 }}>
                                <div className="ant-upload-select" style={{ width: '100%', height: '100%' }}>
                                    <Upload
                                        accept={accept || undefined}
                                        multiple
                                        showUploadList={false}
                                        beforeUpload={handleBeforeUpload}
                                    >
                                        <div style={{
                                            width: '104px',
                                            height: '104px',
                                            border: `1px dashed ${isDarkMode ? '#434343' : '#d9d9d9'}`,
                                            borderRadius: '8px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: uploading ? 'not-allowed' : 'pointer',
                                            backgroundColor: isDarkMode ? '#2a2a2a' : undefined
                                        }}>
                                            {uploading ? (
                                                <LoadingOutlined style={{ color: isDarkMode ? '#fff' : undefined }} />
                                            ) : (
                                                <>
                                                    <PlusOutlined style={{ color: isDarkMode ? '#fff' : undefined }} />
                                                    <div style={{ marginTop: 8, color: isDarkMode ? '#fff' : undefined }}>{t('upload')}</div>
                                                </>
                                            )}
                                        </div>
                                    </Upload>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </>
    );
};

export default FooterPreview;
